// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { isPlanLimitError } from '@/account/utilities/is-plan-limit-error';
import {
  type AttachmentOwnerType,
  DeleteOwnerAttachmentDialog,
} from '@/components/attachment-delete-dialog';
import {
  AttachmentInlineList,
  type AttachmentInlineListItem,
  AttachmentInlineListSkeleton,
} from '@/components/attachment-inline-list';
import {
  AttachmentUploadZone,
  AttachmentUploadZoneHandle,
  AttachmentUploadZoneSkeleton,
} from '@/components/attachment-upload-zone';
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { LinkDialog } from '@/components/link-dialog';
import { LinkInputButton } from '@/components/link-input-button';
import { SupportSnackbar } from '@/components/support-snackbar';
import { UploadErrorDialog } from '@/components/upload-error-dialog';
import { isFileReadError } from '@/file/api/is-file-read-error';
import {
  File as EntityFile,
  fileToAttachment,
  urlLinkToAttachment,
} from '@/file/api/types';
import { useDisclosure } from '@/hooks/use-disclosure';
import { deleteURLLink } from '@/url-link/api/delete-url-link';
import { AddURLLinkParams, URLLink } from '@/url-link/api/types';
import {
  UpdateURLLinkParams,
  updateURLLink,
} from '@/url-link/api/update-url-link';
import { Box, Stack, Typography } from '@mui/material';
import { captureException } from '@sentry/react';
import { QueryKey, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';

const ATTACHMENT_LIST_MAX_HEIGHT_PX = 360;

/**
 * Return type for entity file hooks.
 */
interface EntityAttachmentsFilesData {
  files: EntityFile[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * Return type for entity URL link hooks.
 */
interface EntityAttachmentsURLLinksData {
  urlLinks: URLLink[];
  isLoading: boolean;
  isError: boolean;
}

interface Props {
  /** The key of the entity (used for file delete and link mutations) */
  entityKey: string;
  /** The ID of the entity (used for uploads) */
  entityId: string;
  /** Owner label used in the delete confirmation dialogs */
  ownerType: AttachmentOwnerType;
  /** Whether the delete confirmation dialogs mention the audit log record */
  includeAuditNote?: boolean;
  /** Hook return value containing files, loading, and error states */
  filesData: EntityAttachmentsFilesData;
  /** Hook return value containing URL links, loading, and error states */
  urlLinksData: EntityAttachmentsURLLinksData;
  /** Function to upload a file to the entity */
  uploadFile: (entityId: string, file: globalThis.File) => Promise<unknown>;
  /** Function to add a URL link to the entity */
  addURLLink: (params: AddURLLinkParams) => Promise<unknown>;
  /** Function to delete a file from the entity by key */
  deleteFile: (entityKey: string, fileKey: string) => Promise<unknown>;
  /** Query keys to invalidate after any attachment mutation */
  queryKeysToInvalidate: QueryKey[];
}

function EntityAttachmentsSkeleton() {
  return (
    <HardfinTabCardWrapper title="Files and links" hideDivider>
      <AttachmentInlineListSkeleton />
      <AttachmentUploadZoneSkeleton />
    </HardfinTabCardWrapper>
  );
}

/**
 * Shared section component for displaying and managing files and URL links
 * on an entity detail page. Rendered inline in the right column. Each entity
 * domain wraps this component to bind its API functions and query keys.
 */
export function EntityAttachmentsSection(props: Props) {
  const queryClient = useQueryClient();
  const uploadZoneRef = useRef<AttachmentUploadZoneHandle>(null);
  const [deletingFile, setDeletingFile] = useState<EntityFile | null>(null);
  const [deletingURLLink, setDeletingURLLink] = useState<URLLink | null>(null);
  const [editingURLLink, setEditingURLLink] = useState<URLLink | null>(null);
  const [showUploadError, setShowUploadError] = useState(false);
  const deleteFileDialog = useDisclosure();
  const deleteURLLinkDialog = useDisclosure();
  const editURLLinkDialog = useDisclosure();
  const uploadErrorDialog = useDisclosure();

  const invalidateAttachments = () => {
    for (const queryKey of props.queryKeysToInvalidate) {
      queryClient.invalidateQueries({ queryKey });
    }
  };

  const uploadFileMutation = useMutation({
    mutationFn: (file: globalThis.File) =>
      props.uploadFile(props.entityId, file),
    onSuccess: () => {
      invalidateAttachments();
    },
    onError: (error) => {
      if (isPlanLimitError(error)) {
        return;
      }

      if (isFileReadError(error)) {
        uploadErrorDialog.open();
      } else {
        captureException(error);
        setShowUploadError(true);
      }
    },
  });

  const addURLLinkMutation = useMutation({
    mutationFn: (params: AddURLLinkParams) => props.addURLLink(params),
    onSuccess: () => {
      invalidateAttachments();
    },
    onError: (error) => {
      if (isPlanLimitError(error)) {
        return;
      }

      captureException(error);
    },
  });

  const deleteFileMutation = useMutation({
    mutationFn: (file: EntityFile) =>
      props.deleteFile(props.entityKey, file.key),
    onSuccess: () => {
      invalidateAttachments();
      deleteFileDialog.close();
      setDeletingFile(null);
    },
    onError: (error) => {
      captureException(error);
    },
  });

  const deleteURLLinkMutation = useMutation({
    mutationFn: (urlLink: URLLink) => deleteURLLink(urlLink.key),
    onSuccess: () => {
      invalidateAttachments();
      deleteURLLinkDialog.close();
      setDeletingURLLink(null);
    },
    onError: (error) => {
      captureException(error);
    },
  });

  const updateURLLinkMutation = useMutation({
    mutationFn: (params: UpdateURLLinkParams) => updateURLLink(params),
    onSuccess: () => {
      invalidateAttachments();
      editURLLinkDialog.close();
      setEditingURLLink(null);
    },
    onError: (error) => {
      captureException(error);
    },
  });

  const files = props.filesData.files;
  const urlLinks = props.urlLinksData.urlLinks;
  const isLoading = props.filesData.isLoading || props.urlLinksData.isLoading;
  const isError = props.filesData.isError || props.urlLinksData.isError;

  const handleDeleteFile = (file: EntityFile) => {
    setDeletingFile(file);
    deleteFileDialog.open();
  };

  const handleDeleteURLLink = (urlLink: URLLink) => {
    setDeletingURLLink(urlLink);
    deleteURLLinkDialog.open();
  };

  const handleEditURLLink = (urlLink: URLLink) => {
    setEditingURLLink(urlLink);
    editURLLinkDialog.open();
  };

  const attachmentItems: AttachmentInlineListItem[] = [
    ...files.map((file) => ({
      attachment: fileToAttachment(file),
      onDelete: () => handleDeleteFile(file),
    })),
    ...urlLinks.map((urlLink) => ({
      attachment: urlLinkToAttachment(urlLink),
      onDelete: () => handleDeleteURLLink(urlLink),
      onEdit: () => handleEditURLLink(urlLink),
    })),
  ].sort(
    (a, b) => b.attachment.datedAt.toMillis() - a.attachment.datedAt.toMillis()
  );

  const confirmDeleteFile = () => {
    if (deletingFile) {
      deleteFileMutation.mutate(deletingFile);
    }
  };

  const confirmDeleteURLLink = () => {
    if (deletingURLLink) {
      deleteURLLinkMutation.mutate(deletingURLLink);
    }
  };

  const handleUpdateURLLink = (params: UpdateURLLinkParams) => {
    updateURLLinkMutation.mutate(params);
  };

  const handleFilesSelected = (selectedFiles: globalThis.File[]) => {
    for (const file of selectedFiles) {
      uploadFileMutation.mutate(file);
    }
  };

  if (isLoading) {
    return <EntityAttachmentsSkeleton />;
  }

  return (
    <Stack>
      <SupportSnackbar
        isOpen={uploadFileMutation.isError && showUploadError}
        onClose={() => {
          uploadFileMutation.reset();
          setShowUploadError(false);
        }}
        message="An error occurred while uploading the file"
      />
      <SupportSnackbar
        isOpen={addURLLinkMutation.isError}
        onClose={addURLLinkMutation.reset}
        message="An error occurred while adding the link"
      />
      <SupportSnackbar
        isOpen={deleteFileMutation.isError}
        onClose={deleteFileMutation.reset}
        message="An error occurred while deleting the file"
      />
      <SupportSnackbar
        isOpen={deleteURLLinkMutation.isError}
        onClose={deleteURLLinkMutation.reset}
        message="An error occurred while deleting the link"
      />
      <SupportSnackbar
        isOpen={updateURLLinkMutation.isError}
        onClose={updateURLLinkMutation.reset}
        message="An error occurred while updating the link"
      />
      <UploadErrorDialog
        isOpen={uploadErrorDialog.isOpen}
        onClose={uploadErrorDialog.close}
        onRetry={() => {
          uploadErrorDialog.close();
          uploadZoneRef.current?.openFilePicker();
        }}
      />
      <DeleteOwnerAttachmentDialog
        attachmentType="file"
        includeAuditNote={props.includeAuditNote}
        isPending={deleteFileMutation.isPending}
        isOpen={deleteFileDialog.isOpen}
        onClose={deleteFileDialog.close}
        onConfirm={confirmDeleteFile}
        ownerType={props.ownerType}
      />
      <DeleteOwnerAttachmentDialog
        attachmentType="link"
        includeAuditNote={props.includeAuditNote}
        isPending={deleteURLLinkMutation.isPending}
        isOpen={deleteURLLinkDialog.isOpen}
        onClose={deleteURLLinkDialog.close}
        onConfirm={confirmDeleteURLLink}
        ownerType={props.ownerType}
      />
      <LinkDialog
        open={editURLLinkDialog.isOpen}
        onClose={editURLLinkDialog.close}
        editLink={editingURLLink}
        onAddLink={() => undefined}
        onEditLink={handleUpdateURLLink}
      />

      <HardfinTabCardWrapper
        title="Files and links"
        hideDivider
        actions={
          <LinkInputButton
            size="small"
            variant="text"
            onAddLink={(name, url) => {
              addURLLinkMutation.mutate({
                entityKey: props.entityKey,
                name,
                url,
              });
            }}
            disabled={addURLLinkMutation.isPending}
          >
            Add link
          </LinkInputButton>
        }
      >
        {isError ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: 'italic', padding: 1 }}
          >
            Unable to load files and links
          </Typography>
        ) : attachmentItems.length > 0 ? (
          <Box
            sx={{
              maxHeight: { lg: ATTACHMENT_LIST_MAX_HEIGHT_PX },
              overflowY: { lg: 'auto' },
              overscrollBehavior: { lg: 'contain' },
            }}
          >
            <AttachmentInlineList
              items={attachmentItems}
              deleteDisabled={
                deleteFileMutation.isPending || deleteURLLinkMutation.isPending
              }
            />
          </Box>
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            marginBottom={2}
            padding={1}
          >
            No files or links attached
          </Typography>
        )}

        <AttachmentUploadZone
          ref={uploadZoneRef}
          isUploading={uploadFileMutation.isPending}
          multiple={false}
          onFilesSelected={handleFilesSelected}
          uploadLabel="Upload file"
        />
      </HardfinTabCardWrapper>
    </Stack>
  );
}
